import { menuType } from '@/class/MenuData'
import { Menu, MenuMode, MenuProps, MenuTheme } from 'ant-design-vue'
import { PropType, VNode, defineComponent, h } from 'vue'

const { Item, SubMenu } = Menu

export default defineComponent({
  name: 'SiderMenu',
  props: {
    menu: {
      type: Array as PropType<menuType[]>,
      required: true
    },
    theme: {
      type: String as PropType<MenuTheme>,
      required: false,
      default: 'dark'
    },
    mode: {
      type: String as PropType<MenuMode>,
      required: false,
      default: 'inline'
    },
    collapsed: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedKeys: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      currentSelectedKeys: [],
      openKeys: [],
      cachedOpenKeys: []
    } as {
      currentSelectedKeys: string[],
      openKeys: string[],
      cachedOpenKeys: string[]
    }
  },
  emits: ['select'],
  computed: {
    rootSubmenuKeys () {
      const keys: string[] = []
      this.menu.forEach(item => keys.push(item.path))
      return keys
    }
  },
  watch: {
    collapsed (val) {
      if (val) {
        this.cachedOpenKeys = this.openKeys.concat()
        this.openKeys = []
      } else {
        this.openKeys = this.cachedOpenKeys
      }
    },
    selectedKeys: {
      immediate: true,
      handler(val) {
        if (this.currentSelectedKeys !== val) {
          this.currentSelectedKeys = val
        }
      }
    },
    $route: function () {
      this.updateOpenKeys()
    }
  },
  mounted () {
    this.updateOpenKeys()
  },
  methods: {
    updateOpenKeys () {
      const routes = this.$route.matched.concat()
      const openKeys: string[] = []
      if (this.mode === 'inline') {
        routes.forEach(item => {
          openKeys.push(item.path)
        })
      }
      this.collapsed ? (this.cachedOpenKeys = openKeys) : (this.openKeys = openKeys)
    },
    onOpenChange (openKeys: string[]) {
      // 在水平模式下时执行，并且不再执行后续
      if (this.mode === 'horizontal') {
        this.openKeys = openKeys
        return
      }
      // 非水平模式时
      const latestOpenKey = openKeys.find(key => !this.openKeys.includes(key))
      if (!this.rootSubmenuKeys.includes(latestOpenKey!)) {
        this.openKeys = openKeys
      } else {
        this.openKeys = latestOpenKey ? [latestOpenKey] : []
      }
    },
    // render
    renderItem (menu: menuType) {
      if (!menu.meta.hidden) {
        return menu.children && menu.children.length > 0 ? this.renderSubMenu(menu) : this.renderMenuItem(menu)
      }
      return null
    },
    renderIcon(icon: undefined | (() => VNode)) {
      if (icon) {
        return icon()
      } else {
        return null
      }
    },
    getName(menu: menuType) {
      if (typeof menu.meta.name !== 'function') {
        return menu.meta.name
      } else {
        return menu.meta.name()
      }
    },
    renderMenuItem (menu: menuType) {
      return h(Item, { key: menu.path }, {
        default: () => this.getName(menu),
        icon: () => this.renderIcon(menu.meta.icon)
      })
    },
    renderSubMenu (menu: menuType) {
      const list: (VNode | null)[] = []
      menu.children!.forEach(item => {
        const menuItem = this.renderItem(item)
        if (menuItem) {
          list.push(menuItem)
        }
      })
      if (list.length > 0) {
        return h(SubMenu, { key: menu.path, title: this.getName(menu) }, {
          default: () => list,
          icon: () => this.renderIcon(menu.meta.icon)
        })
      } else {
        return null
      }
    }
  },
  render() {
    const { mode, theme, menu } = this
    const props: MenuProps = {
      mode: mode,
      theme: theme,
      inlineCollapsed: this.collapsed,
      selectedKeys: this.currentSelectedKeys,
      openKeys: this.openKeys,
      onSelect: ({ selectedKeys, key }) => {
        if ((key as string).indexOf('$url:') === 0) {
          const url = (key as string).slice(5)
          this.$emit('select', 'url', url)
        } else {
          this.currentSelectedKeys = selectedKeys as string[]
          this.$emit('select', 'menu', key)
        }
      }
    }
    const menuTree = menu.map(item => {
      return this.renderItem(item)
    })
    return h(Menu, props, {
      default: () => menuTree
    })
  }
})
